import React from 'react'
import { Box, Dialog, DialogContent, IconButton, Typography, TextField, Button } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { useLocale, useTranslations } from 'next-intl'

import { CountryWithMCC } from '@/src/api/rtkQuery/countryApi'
import ItineraryAutoComplete from '@/src/components/common/itinerary/auto-complete'
import GoogleSignInButton from '@/src/components/common/GoogleSignInButton'
import GoogleIcon from './GoogleIcon'
import { UITreatment } from '@/src/services/otpErrorMapping'
import { GoogleUserData } from '@/src/types/auth'

import { OtpStep } from './index'
import { OTP_MODAL_BENEFITS } from './constants'

const OtpInput = MuiOtpInput as any

// Shared TextField styling for disabled Google user fields
const disabledTextFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: 36,
        fontSize: 14,
        bgcolor: 'grey.100',
        '& fieldset': {
            borderColor: 'grey.300',
            borderRadius: '4px'
        }
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 12px',
        fontSize: 14
    }
}

interface FlowData {
    currentStep: OtpStep
    error: string
    errorState: {
        message: string
        uiTreatment?: UITreatment
        field?: 'email' | 'mobile' | 'countryCode' | 'otp'
    }
    isWelcomeBack: boolean
    existingEmail: string | null
    maskedEmail: string | null
    isGoogleFlow: boolean
    googleUserData: GoogleUserData | null

    // Hero section content (computed in Logic Layer)
    heroTitle: string
    heroSubtitle: string
    heroBrandName: string

    // Mobile data
    mobileCountryCode: string
    selectedCountryCode: CountryWithMCC | null
    countryData: CountryWithMCC[] | undefined
    isCountryDataLoading: boolean
    mobileNumber: string
    mobileOtp: string

    // Email data
    email: string
    emailOtp: string

    // Loading states
    isSendingOtp: boolean
    isVerifyingOtp: boolean

    // Timer states
    mobileResendTimer: number
    emailResendTimer: number
}

interface FlowHandlers {
    onClose: () => void

    // Mobile handlers
    handleCountryCodeChange: (country: CountryWithMCC | null) => void
    handleMobileNumberChange: (value: string) => void
    handleMobileOtpChange: (value: string) => void
    handleSendMobileOtp: () => void
    handleVerifyMobileOtp: () => void
    handleResendMobileOtp: () => void
    handleBackToMobileInput: () => void

    // Email handlers
    handleEmailChange: (value: string) => void
    handleEmailOtpChange: (value: string) => void
    handleSendEmailOtp: () => void
    handleVerifyEmailOtp: () => void
    handleResendEmailOtp: () => void
    handleBackToEmailInput: () => void

    // Google Sign In handlers
    handleGoogleSignIn: (userData: GoogleUserData) => void
    handleSendGoogleMobileOtp: () => void
    handleVerifyGoogleMobileOtp: () => void
    handleResendGoogleMobileOtp: () => void
}

interface OtpVerificationModalDesktopProps {
    open: boolean
    flowData: FlowData
    flowHandlers: FlowHandlers
}

const OtpVerificationModalDesktop: React.FC<OtpVerificationModalDesktopProps> = ({ open, flowData, flowHandlers }) => {
    const {
        currentStep,
        error,
        errorState,
        isWelcomeBack,
        existingEmail,
        maskedEmail,
        isGoogleFlow,
        googleUserData,
        mobileCountryCode,
        selectedCountryCode,
        countryData,
        isCountryDataLoading,
        mobileNumber,
        mobileOtp,
        email,
        emailOtp,
        isSendingOtp,
        isVerifyingOtp,
        mobileResendTimer,
        emailResendTimer
    } = flowData

    const {
        onClose,
        handleCountryCodeChange,
        handleMobileNumberChange,
        handleMobileOtpChange,
        handleSendMobileOtp,
        handleVerifyMobileOtp,
        handleResendMobileOtp,
        handleBackToMobileInput,
        handleEmailChange,
        handleEmailOtpChange,
        handleSendEmailOtp,
        handleVerifyEmailOtp,
        handleResendEmailOtp,
        handleBackToEmailInput,
        handleGoogleSignIn,
        handleSendGoogleMobileOtp,
        handleVerifyGoogleMobileOtp,
        handleResendGoogleMobileOtp
    } = flowHandlers

    const locale = useLocale()
    const t = useTranslations('otpModal')

    const renderMobileInputStep = () => renderMobileInputFields(false)

    const renderMobileVerificationStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
                {t('mobileOtpDescription', { mobileCountryCode, mobileNumber })}
            </Typography>

            <OtpInput
                value={mobileOtp}
                onChange={(value: string) => {
                    handleMobileOtpChange(value)
                }}
                onComplete={(value: string) => {
                    // Auto-verify when all 4 digits are entered
                    if (value.length === 4) {
                        handleVerifyMobileOtp()
                    }
                }}
                length={4}
                autoFocus
                TextFieldsProps={{
                    size: 'small'
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        width: 50,
                        height: 50,
                        '& fieldset': {
                            borderColor: 'grey.300',
                            borderRadius: '8px',
                            borderWidth: 2
                        },
                        '&:hover fieldset': {
                            borderColor: 'primary.main'
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2
                        }
                    },
                    '& .MuiOutlinedInput-input': {
                        textAlign: 'center',
                        fontSize: 18,
                        fontWeight: 500
                    }
                }}
            />

            {error && (
                <Typography sx={{ color: 'error.main', fontSize: 12 }} role="alert" aria-live="assertive">
                    {error}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={isGoogleFlow ? handleVerifyGoogleMobileOtp : handleVerifyMobileOtp}
                disabled={isVerifyingOtp}
                sx={{
                    height: 40,
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '4px'
                }}
            >
                {isVerifyingOtp ? t('verifying') : t('verifyOtp')}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                    component="span"
                    onClick={handleBackToMobileInput}
                    sx={{
                        fontSize: 12,
                        color: 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    <EditIcon sx={{ fontSize: 14 }} />
                    {t('changeNumber')}
                </Typography>
                <Typography
                    component="span"
                    onClick={mobileResendTimer === 0 ? handleResendMobileOtp : undefined}
                    sx={{
                        fontSize: 12,
                        color: mobileResendTimer === 0 ? 'primary.main' : 'text.disabled',
                        cursor: mobileResendTimer === 0 ? 'pointer' : 'default',
                        textDecoration: 'none'
                    }}
                >
                    {mobileResendTimer > 0 ? t('resendOtpTimer', { timer: mobileResendTimer }) : t('resendOtp')}
                </Typography>
            </Box>
        </Box>
    )

    const renderEmailInputStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {isWelcomeBack && maskedEmail ? (
                <Typography variant="body2" sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}>
                    {t('welcomeBackMessage', { email: maskedEmail })}
                </Typography>
            ) : !existingEmail ? (
                <Typography variant="body2" sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}>
                    {t('enterEmailToComplete')}
                </Typography>
            ) : (
                <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {t('emailVerificationDescription')}
                </Typography>
            )}

            <TextField
                placeholder={t('emailAddress')}
                type="email"
                value={email}
                onChange={e => handleEmailChange(e.target.value)}
                fullWidth
                disabled={isWelcomeBack && !!existingEmail}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: 36,
                        fontSize: 14,
                        '& fieldset': {
                            borderColor: 'grey.300',
                            borderRadius: '4px'
                        }
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                        fontSize: 14
                    }
                }}
            />

            {error && (
                <Typography sx={{ color: 'error.main', fontSize: 12 }} role="alert" aria-live="assertive">
                    {error}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSendEmailOtp}
                disabled={isSendingOtp}
                sx={{
                    height: 40,
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '4px'
                }}
            >
                {isSendingOtp ? t('sending') : t('sendVerificationCode')}
            </Button>

            {!isWelcomeBack && (
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        height: 40,
                        fontSize: 14,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.50'
                        }
                    }}
                >
                    {t('skipForNow')}
                </Button>
            )}
        </Box>
    )

    const renderEmailVerificationStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
                {t('emailOtpDescription', { email })}
            </Typography>

            <OtpInput
                value={emailOtp}
                onChange={(value: string) => {
                    handleEmailOtpChange(value)
                }}
                onComplete={(value: string) => {
                    // Auto-verify when all 4 digits are entered
                    if (value.length === 4) {
                        handleVerifyEmailOtp()
                    }
                }}
                length={4}
                autoFocus
                TextFieldsProps={{
                    size: 'small'
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        width: 50,
                        height: 50,
                        '& fieldset': {
                            borderColor: 'grey.300',
                            borderRadius: '8px',
                            borderWidth: 2
                        },
                        '&:hover fieldset': {
                            borderColor: 'primary.main'
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2
                        }
                    },
                    '& .MuiOutlinedInput-input': {
                        textAlign: 'center',
                        fontSize: 18,
                        fontWeight: 500
                    }
                }}
            />

            {error && (
                <Typography sx={{ color: 'error.main', fontSize: 12 }} role="alert" aria-live="assertive">
                    {error}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleVerifyEmailOtp}
                disabled={isVerifyingOtp || errorState?.uiTreatment === UITreatment.DISABLE_SEND_BUTTON}
                sx={{
                    height: 40,
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '4px'
                }}
            >
                {isVerifyingOtp ? t('verifying') : t('completeVerification')}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                    component="span"
                    onClick={handleBackToEmailInput}
                    sx={{
                        fontSize: 12,
                        color: 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    <EditIcon sx={{ fontSize: 14 }} />
                    {t('edit')}
                </Typography>
                <Typography
                    component="span"
                    onClick={
                        emailResendTimer === 0 || errorState?.uiTreatment === UITreatment.SHOW_RESEND_BUTTON
                            ? handleResendEmailOtp
                            : undefined
                    }
                    sx={{
                        fontSize: 12,
                        color:
                            emailResendTimer === 0 || errorState?.uiTreatment === UITreatment.SHOW_RESEND_BUTTON
                                ? 'primary.main'
                                : 'text.disabled',
                        cursor:
                            emailResendTimer === 0 || errorState?.uiTreatment === UITreatment.SHOW_RESEND_BUTTON
                                ? 'pointer'
                                : 'default',
                        textDecoration: 'none'
                    }}
                >
                    {emailResendTimer > 0 ? t('resendCodeTimer', { timer: emailResendTimer }) : t('resendCode')}
                </Typography>
            </Box>
        </Box>
    )

    // Shared mobile input fields component
    const renderMobileInputFields = (showGoogleFields: boolean) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', mb: showGoogleFields ? 0.5 : 0 }}>
                {showGoogleFields ? t('provideMobileNumberDescription') : t('mobileVerificationDescription')}
            </Typography>

            {showGoogleFields && renderGoogleUserFields()}

            {/* Mobile number input fields */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box
                    sx={{
                        width: 100,
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            fontSize: 12
                        }
                    }}
                >
                    <ItineraryAutoComplete
                        label=""
                        disableClearable
                        loading={isCountryDataLoading}
                        options={countryData || []}
                        value={selectedCountryCode as any}
                        onChange={(value: any) => handleCountryCodeChange(value as CountryWithMCC | null)}
                        getOptionLabel={(option: any) => {
                            const country = option as CountryWithMCC
                            return `+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`
                        }}
                        renderValueForMobileScreen={(value: any) => {
                            const country = value as CountryWithMCC
                            return `+ ${country?.mobileCountryCode}`
                        }}
                        renderOption={(props: any, option: any) => {
                            const country = option as CountryWithMCC
                            return (
                                <Typography {...props} key={props.key} sx={{ fontSize: 13 }}>
                                    {`+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`}
                                </Typography>
                            )
                        }}
                    />
                </Box>

                <TextField
                    placeholder={t('enterMobileNumber')}
                    type="tel"
                    value={mobileNumber}
                    onChange={e => handleMobileNumberChange(e.target.value)}
                    fullWidth
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            fontSize: 14,
                            '& fieldset': {
                                borderColor: 'grey.300',
                                borderRadius: '4px'
                            }
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '8px 12px',
                            fontSize: 14
                        }
                    }}
                />
            </Box>

            {error && (
                <Typography sx={{ color: 'error.main', fontSize: 12 }} role="alert" aria-live="assertive">
                    {error}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={showGoogleFields ? handleSendGoogleMobileOtp : handleSendMobileOtp}
                disabled={isSendingOtp}
                sx={{
                    height: 40,
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '4px'
                }}
            >
                {isSendingOtp ? t('sending') : t('sendOtp')}
            </Button>

            {!showGoogleFields && (
                <GoogleSignInButton
                    onSuccess={handleGoogleSignIn}
                    onError={() => {
                        // Error handled by GoogleSignInButton component
                    }}
                    buttonText={t('continueWithGoogle')}
                    fullWidth
                    sx={{
                        height: 40,
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                            backgroundColor: 'grey.50',
                            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)'
                        }
                    }}
                    startIcon={<GoogleIcon style={{ width: '18px', height: '18px' }} />}
                />
            )}
        </Box>
    )

    // Shared component for Google user fields
    const renderGoogleUserFields = () => (
        <>
            {/* First Name and Last Name - 50/50 row layout */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                    label={t('firstName')}
                    value={googleUserData?.firstName || 'N/A'}
                    disabled
                    sx={{ ...disabledTextFieldSx, flex: 1 }}
                />

                <TextField
                    label={t('lastName')}
                    value={googleUserData?.lastName || 'N/A'}
                    disabled
                    sx={{ ...disabledTextFieldSx, flex: 1 }}
                />
            </Box>

            {/* Email - Pre-filled and Disabled */}
            <TextField label={t('email')} value={email} disabled fullWidth sx={disabledTextFieldSx} />
        </>
    )

    const renderGoogleMobileInputStep = () => renderMobileInputFields(true)

    const renderCurrentStep = () => {
        switch (currentStep) {
            case OtpStep.MOBILE_INPUT:
                return renderMobileInputStep()
            case OtpStep.MOBILE_VERIFICATION:
                return renderMobileVerificationStep()
            case OtpStep.EMAIL_INPUT:
                return renderEmailInputStep()
            case OtpStep.EMAIL_VERIFICATION:
                return renderEmailVerificationStep()
            case OtpStep.GOOGLE_MOBILE_INPUT:
                return renderGoogleMobileInputStep()
            default:
                return renderMobileInputStep()
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-container': {
                    m: '-14px !important'
                },
                '& .MuiDialog-paper': {
                    width: { xs: '100%', sm: 700 },
                    maxWidth: { xs: '100%', sm: 820 },
                    height: { xs: 'auto', sm: 400 },
                    maxHeight: { xs: 'none', sm: 400 },
                    borderRadius: { xs: 0, sm: '8px' }
                }
            }}
        >
            <DialogContent sx={{ display: 'flex', p: 0, height: '100%' }}>
                {/* Left: Benefits Section (hidden on mobile) */}
                <Box
                    sx={{
                        display: { xs: 'none', sm: 'none', md: 'flex' },
                        bgcolor: 'primary.main',
                        background: theme =>
                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'common.white',
                        pl: 5,
                        pr: 6,
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, fontSize: 22 }}>
                        {t('registerWithFlyin')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, fontSize: 13, opacity: 0.9 }}>
                        {t('getFollowingBenefits')}
                    </Typography>
                    <Box component="ul" sx={{ pl: 0, m: 0, listStyle: 'none' }}>
                        {OTP_MODAL_BENEFITS.map((benefit, index) => (
                            <Box
                                component="li"
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2.5,
                                    '&:last-child': { mb: 0 }
                                }}
                            >
                                <Box
                                    sx={{
                                        mr: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: 'common.white',
                                        opacity: 0.9
                                    }}
                                >
                                    {benefit.icon}
                                </Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 400, color: 'common.white' }}>
                                    {t(benefit.textKey)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Right: OTP Verification Flow */}
                <Box
                    sx={{
                        flex: 1.2,
                        px: { xs: 1.5, sm: 3 },
                        pb: { xs: 1.5, sm: 2.5 },
                        pt: { xs: 0.5, sm: 1.5 },
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: { xs: 'auto', sm: 300 },
                        position: 'relative'
                    }}
                >
                    {/* Header with close button */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mb: { xs: 1.5, sm: 2 },
                            mr: { xs: 0, sm: -1.5 }
                        }}
                    >
                        <IconButton onClick={onClose} sx={{ p: 0.5 }} aria-label={t('close')}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Title */}
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18, mb: { xs: 0.25, sm: 0.5 } }}>
                        {currentStep === OtpStep.MOBILE_INPUT && t('enterYourMobileNumber')}
                        {currentStep === OtpStep.MOBILE_VERIFICATION && t('verifyMobileNumber')}
                        {currentStep === OtpStep.EMAIL_INPUT &&
                            (isWelcomeBack ? t('welcomeBack') : t('completeRegistration'))}
                        {currentStep === OtpStep.EMAIL_VERIFICATION && t('verifyEmailAddress')}
                        {currentStep === OtpStep.GOOGLE_MOBILE_INPUT && t('completeRegistration')}
                    </Typography>

                    {/* Subtitle for Welcome Back flow */}
                    {currentStep === OtpStep.EMAIL_INPUT && isWelcomeBack && (
                        <Typography
                            variant="body2"
                            sx={{ fontSize: 14, fontWeight: 400, color: 'text.primary', mb: 1 }}
                        >
                            {t('verifyEmailAddressSubtitle')}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {renderCurrentStep()}
                    </Box>

                    {/* Bottom Section - Only show for non-Google flows */}
                    {!isGoogleFlow && (
                        <Box
                            sx={{ mt: 'auto', borderTop: '1px solid', borderColor: 'grey.300', pt: { xs: 1, sm: 1.5 } }}
                        >
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
                                {t('termsAndPrivacy')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export { OtpVerificationModalDesktop }
export type { OtpVerificationModalDesktopProps }
